import { type Context, createContext, type JSX, type PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import type { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'
import { useSpinnerDelay } from '@dhl/use-spinner-delay'
import type { AppError } from '@dhl/receiver-profile-api'
import { useGetProfilePictureUrl } from './use-get-profile-picture-url.js'

/**
 * Only really generic context, not specific for a certain flow/route
 */
export type AppState = {
  /**
   * When the user has a profile image set we will show this
   */
  profileAvatar?: string

  /**
   * When we are actually loading our profile avatar
   */
  isLoadingProfileAvatar: boolean
}

type Actions = {
  /**
   * TODO: ask backend for better backwards compatible contract, now its just string ...
   * Normally we never propogate refetching stuff from components to components but just
   * invalidating query keys. As soon as the api is adjusted for the profile its url to not
   * return 404 when they have no image but just a better json in both cases we can remove
   * this refetcher, the logic to not enable the query after an error.
   */
  refetchAvatar: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<string | undefined, AppError | null>>
}

export type ContextInput = AppState & Actions

export const defaultValue = {}

/**
 * Alert: we only export this for our unit tests, your app should not import this and use `useApp()` instead.
 */
export const AppContext: Context<ContextInput> = createContext<ContextInput>(defaultValue as ContextInput)

type TasksProviderProps = PropsWithChildren

export function AppProvider({ children }: TasksProviderProps): JSX.Element {
  const isPictureEnabled = useRef(true)

  const {
    data: avatarUrl,
    isLoading: isLoadingProfileAvatar,
    isError,
    refetch: refetchAvatar,
  } = useGetProfilePictureUrl(isPictureEnabled.current)

  const avatarLoadingDelayed = useSpinnerDelay(isLoadingProfileAvatar)

  /**
   * An error on the profile picture endpoint means the user has no image
   * It would have been nicer the api returned it or told us its not there.
   *
   * Now we have to guard it ourselves, since an error will allways be retrieved
   * in React Query the next time it mounts, even when setting all gc and mount options correctly.
   */
  useEffect(() => {
    if (isError) {
      isPictureEnabled.current = false
    }
  }, [isError])

  const value = useMemo(
    (): ContextInput => ({
      profileAvatar: !avatarLoadingDelayed ? avatarUrl : undefined,
      isLoadingProfileAvatar: avatarLoadingDelayed,
      refetchAvatar,
    }),

    [avatarUrl, avatarLoadingDelayed, refetchAvatar]
  )

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}
